"use client";

import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { STATUS_IDS } from "@/lib/constants/statuses";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/utils";
import Image from "next/image";
import { AccountingArticleMapping } from "@/components/admin/shared/accounting-article-mapping";

const formSchema = z.object({
  name: z.string().min(1, "Name is required").max(255),
  category_id: z.number().min(1, "Category is required"),
  image_url: z.string().nullable(),
  status_id: z.number(),
});

type FormValues = z.infer<typeof formSchema>;

interface Category {
  id: number;
  name: string;
}

interface ShopItem {
  id: number;
  venue_id: number;
  name: string;
  category_id: number;
  pricing_id?: number;
  image_url: string | null;
  is_active: boolean;
  status_id: number;
  article_code?: string | null;
  pricing: {
    id: number;
    price: number;
    vat_percentage: number;
    currency: string;
  } | null;
  category: {
    id: number;
    name: string;
  } | null;
  created_at: string;
  updated_at: string;
}

interface ShopItemFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  venueId: number;
  shopItem: ShopItem | null;
  onSuccess: (createdItem?: ShopItem) => void;
}

export function ShopItemFormDialog({
  open,
  onOpenChange,
  venueId,
  shopItem,
  onSuccess,
}: ShopItemFormDialogProps) {
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: shopItem?.name || "",
      category_id: shopItem?.category_id || 0,
      image_url: shopItem?.image_url || null,
      status_id: shopItem?.status_id || STATUS_IDS.SHOP_ITEM_ACTIVE,
    },
  });

  useEffect(() => {
    if (open) {
      fetchCategories();
    }
  }, [open]);

  useEffect(() => {
    if (shopItem) {
      form.reset({
        name: shopItem.name,
        category_id: shopItem.category_id,
        image_url: shopItem.image_url,
        status_id: shopItem.status_id,
      });
      setImagePreview(shopItem.image_url);
    } else {
      form.reset({
        name: "",
        category_id: 0,
        image_url: null,
        status_id: STATUS_IDS.SHOP_ITEM_ACTIVE,
      });
      setImagePreview(null);
    }
  }, [shopItem, form]);

  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      // Use same API endpoint as edit flow for consistency
      const response = await fetch(
        `/api/venues/${venueId}/shop-item-categories`,
      );
      if (!response.ok) throw new Error("Failed to fetch categories");
      const data = await response.json();
      setCategories(data || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error("Failed to load category options");
      // Fallback to standard categories if API fails
      setCategories([
        { id: 1, name: "food" },
        { id: 2, name: "drink" },
        { id: 3, name: "merchandise" },
        { id: 4, name: "other" },
      ]);
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error(
        "Unsupported file type. Please upload an image (JPEG, PNG, WebP, or GIF)",
      );
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size too large. Maximum allowed: 5MB");
      return;
    }

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("bucket", "shop-item-images");

      const response = await fetch("/api/upload", {
        method: "POST",
        body: formData,
        credentials: "same-origin",
      });

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ error: "Failed to upload image" }));
        throw new Error(errorData.error || "Failed to upload image");
      }

      const { url } = await response.json();
      form.setValue("image_url", url);
      setImagePreview(url);
      toast.success("Image uploaded successfully");
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to upload image",
      );
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = () => {
    form.setValue("image_url", null);
    setImagePreview(null);
  };

  const onSubmit = async (values: FormValues) => {
    setLoading(true);
    try {
      const url = shopItem
        ? `/api/venues/${venueId}/shop-items/${shopItem.id}`
        : `/api/venues/${venueId}/shop-items`;
      const method = shopItem ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(values),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to save shop item");
      }

      const responseData = await response.json();
      toast.success(
        `Shop item ${shopItem ? "updated" : "created"} successfully`,
      );

      // For create operations, pass the created item to enable redirect to edit page
      onSuccess(shopItem ? undefined : responseData);
    } catch (error: any) {
      toast.error(error.message || "Failed to save shop item");
    } finally {
      setLoading(false);
    }
  };

  // Form validation - check if required fields are filled
  const formValues = form.watch();
  const isFormValid = formValues.name?.trim() && formValues.category_id > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{shopItem ? "Edit" : "Add"} shop item</DialogTitle>
          <DialogDescription>
            {shopItem
              ? "Update the shop item details."
              : "Create a new shop item for this venue."}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Name <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Cola, T-Shirt, etc." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="category_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Type <span className="text-destructive">*</span>
                  </FormLabel>
                  <Select
                    onValueChange={(value) => field.onChange(parseInt(value))}
                    defaultValue={field.value?.toString()}
                    disabled={loadingCategories}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            loadingCategories ? "Loading..." : "Select type"
                          }
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {categories.map((category) => (
                        <SelectItem
                          key={category.id}
                          value={category.id.toString()}
                        >
                          {category.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormItem>
              <FormLabel>Image</FormLabel>
              <div className="space-y-2">
                {imagePreview ? (
                  <div className="relative w-full h-40 rounded-lg overflow-hidden bg-gray-50 border border-gray-200">
                    <Image
                      src={imagePreview}
                      alt="Shop item preview"
                      fill
                      className="object-contain"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="destructive"
                      className="absolute top-2 right-2"
                      onClick={removeImage}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div
                    className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-primary hover:bg-gray-50 transition-all duration-200"
                    onClick={() =>
                      document.getElementById("shop-item-image-upload")?.click()
                    }
                  >
                    <Upload className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                    <p className="text-sm font-medium text-gray-900 mb-1">
                      Upload shop item image
                    </p>
                    <p className="text-xs text-muted-foreground mb-2">
                      PNG, JPG, GIF up to 5MB
                    </p>
                    <p className="text-xs text-muted-foreground mb-2">
                      Recommended 4:3 (e.g. 1200x900). Images are
                      center-cropped in the UI.
                    </p>
                    <p className="text-xs text-primary">
                      Click to browse files
                    </p>
                    <input
                      id="shop-item-image-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={uploadingImage}
                      className="hidden"
                    />
                  </div>
                )}
                {uploadingImage && (
                  <p className="text-sm text-muted-foreground flex items-center">
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Uploading image...
                  </p>
                )}
              </div>
            </FormItem>

            <FormField
              control={form.control}
              name="status_id"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value === STATUS_IDS.SHOP_ITEM_ACTIVE}
                      onCheckedChange={(checked) =>
                        field.onChange(
                          checked
                            ? STATUS_IDS.SHOP_ITEM_ACTIVE
                            : STATUS_IDS.SHOP_ITEM_ARCHIVED,
                        )
                      }
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Active</FormLabel>
                    <FormDescription>
                      Only active items will be available for purchase
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            {shopItem && (
              <div className="border-t pt-4 mt-4">
                <h4 className="text-sm font-medium mb-3">
                  Accounting integration
                </h4>
                <AccountingArticleMapping
                  venueId={venueId}
                  itemId={shopItem.id}
                  itemType="shop-item"
                  itemName={shopItem.name}
                  currentArticleCode={shopItem.article_code || null}
                />
              </div>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading || loadingCategories || !isFormValid}
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {shopItem ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
